/**
 * cl-core/josa — 받침 판정 조사 (CL fix_2 · 2026-10-15).
 *
 * 순수 TS. 외부 패키지·Node API·'@/' 별칭 0 — Next 와 Deno 양쪽에서 쓴다.
 * 계약은 `src/lib/ko/josa.ts` 와 같다(그 파일은 지우지 않는다). 다른 점은 셋:
 *   ① 라틴 약어·숫자의 «읽는 소리» 받침까지 본다(「SKY은(는)」 실측 버그).
 *   ② 끝의 괄호 묶음은 건너뛰고 앞말로 판정한다(「공공분양주택(본청약)」).
 *   ③ 「은(는)」 같은 병기 자리표시를 본문에서 찾아 고치는 `fixJosaPlaceholders`.
 */

export type JosaPair = '은/는' | '이/가' | '을/를' | '과/와' | '으로/로';

const PAIRS: Record<JosaPair, readonly [string, string]> = {
  '은/는': ['은', '는'],
  '이/가': ['이', '가'],
  '을/를': ['을', '를'],
  '과/와': ['과', '와'],
  '으로/로': ['으로', '로'],
};

/** 받침 판정 결과. `rieul` = 받침이 ㄹ(「으로/로」 에서 「로」를 쓴다). */
export interface Batchim {
  has: boolean;
  rieul: boolean;
}

// 숫자 읽기: 0 영 1 일 2 이 3 삼 4 사 5 오 6 육 7 칠 8 팔 9 구
const DIGIT: Record<string, Batchim> = {
  '0': { has: true, rieul: false }, '1': { has: true, rieul: true }, '2': { has: false, rieul: false },
  '3': { has: true, rieul: false }, '4': { has: false, rieul: false }, '5': { has: false, rieul: false },
  '6': { has: true, rieul: false }, '7': { has: true, rieul: true }, '8': { has: true, rieul: true },
  '9': { has: false, rieul: false },
};

// 알파벳 «이름» 읽기(약어용): L 엘 · M 엠 · N 엔 · R 알 만 받침.
const LETTER_NAME_BATCHIM: Record<string, Batchim> = {
  L: { has: true, rieul: true }, M: { has: true, rieul: false }, N: { has: true, rieul: false }, R: { has: true, rieul: true },
};

function hangulBatchim(code: number): Batchim | null {
  if (code < 0xac00 || code > 0xd7a3) return null;
  const jong = (code - 0xac00) % 28;
  return { has: jong !== 0, rieul: jong === 8 };
}

/** 끝 괄호 묶음을 걷는다. 앞말이 없으면 괄호 안 말을 쓴다. */
function stripTrailingParen(word: string): string {
  let s = word.trim();
  // 끝에 붙은 따옴표·꺾쇠는 판정에서 뺀다
  s = s.replace(/["'」』》〉\]]+$/, '');
  // live1-d(sweep K) — 끝 괄호 묶음이 여럿이면(「더샵 시에르네(조합원취소분)(2회차)」) 남은 끝 괄호가 없어질 때까지 걷는다.
  for (let guard = 0; guard < 4; guard++) {
    const m = /^(.*?)\s*[(（]([^()（）]*)[)）]$/.exec(s);
    if (!m) break;
    const before = m[1].trim();
    if (!before) return m[2].trim();
    s = before;
  }
  return s;
}

/**
 * 마지막 소리의 받침. 판정 불가면 null.
 * 한글 → 음절 · 숫자 → 읽기 · 라틴 대문자 약어(SK·LH) → 알파벳 이름 · 라틴 단어 → l/m/n/ng 만 받침.
 */
export function batchimOf(word: string | null | undefined): Batchim | null {
  const s = stripTrailingParen(String(word ?? ''));
  if (!s) return null;
  const last = s[s.length - 1];
  const h = hangulBatchim(last.charCodeAt(0));
  if (h) return h;
  if (/[0-9]/.test(last)) return DIGIT[last];
  if (/[A-Za-z]/.test(last)) {
    const tail = (/[A-Za-z]+$/.exec(s) || [''])[0];
    const isAcronym = tail.length <= 4 && tail === tail.toUpperCase();
    if (isAcronym) return LETTER_NAME_BATCHIM[last.toUpperCase()] ?? { has: false, rieul: false };
    const low = tail.toLowerCase();
    if (/ng$/.test(low) || /[mn]$/.test(low)) return { has: true, rieul: false };
    if (/l$/.test(low)) return { has: true, rieul: true };
    return { has: false, rieul: false };
  }
  return null;
}

/**
 * seo-blog(2026-10-25) — 끝이 로마 숫자(Ⅰ~Ⅻ · ⅰ~ⅻ)인 이름(「호반써밋 풍무Ⅱ」)의 읽는 소리 받침 — 숫자 읽기(일·이·삼…)와 같은 표.
 *   josa() 의 «판정 불가 → 받침 쪽» 폴백이 「풍무Ⅱ은」 을 만들던 자리만 고친다.
 *   batchimOf·topicSubject(화면 병기 계약 — 「롯데캐슬 Ⅱ은(는)」)는 종전 그대로 둔다.
 */
const ROMAN_VALUE: Record<string, number> = {
  'Ⅰ': 1, 'Ⅱ': 2, 'Ⅲ': 3, 'Ⅳ': 4, 'Ⅴ': 5, 'Ⅵ': 6, 'Ⅶ': 7, 'Ⅷ': 8, 'Ⅸ': 9, 'Ⅹ': 10, 'Ⅺ': 11, 'Ⅻ': 12,
  'ⅰ': 1, 'ⅱ': 2, 'ⅲ': 3, 'ⅳ': 4, 'ⅴ': 5, 'ⅵ': 6, 'ⅶ': 7, 'ⅷ': 8, 'ⅸ': 9, 'ⅹ': 10, 'ⅺ': 11, 'ⅻ': 12,
};
export function romanNumeralBatchim(word: string | null | undefined): Batchim | null {
  const s = stripTrailingParen(String(word ?? ''));
  const v = ROMAN_VALUE[s.slice(-1)];
  if (!v) return null;
  // 10(십) = ㅂ 받침 · 11·12 는 끝자리(일·이) 읽기.
  return v === 10 ? { has: true, rieul: false } : DIGIT[String(v % 10)];
}

/** 받침 유무만. 판정 불가면 null. */
export function hasFinalConsonant(word: string | null | undefined): boolean | null {
  const b = batchimOf(word);
  return b ? b.has : null;
}

/**
 * 화면 문장용 주제격 「은/는」 — 판정 가능하면 단정, 불가면 「은(는)」 병기(hc3 L10).
 * hc3 통합(HC-A-209) — 정의 한 벌. lib/ko/josa·lib/apt/topic-subject 는 이 함수의 재수출.
 * ⛔ josa() 처럼 판정 불가를 받침 쪽으로 떨어뜨리지 않는다(화면에서는 틀린 단정이 병기보다 나쁘다).
 */
export function topicSubject(word: string): string {
  const bat = hasFinalConsonant(word);
  return `${word}${bat === null ? '은(는)' : bat ? '은' : '는'}`;
}

/**
 * 조사 하나를 고른다. 판정 불가면 «받침 있는 쪽»(lib/ko/josa 와 같은 폴백).
 * 「으로/로」 는 받침 ㄹ 이면 「로」.
 */
export function josa(word: string | null | undefined, pair: JosaPair): string {
  const [withB, withoutB] = PAIRS[pair];
  const b = batchimOf(word) ?? romanNumeralBatchim(word);
  if (!b) return withB;
  if (pair === '으로/로') return b.has && !b.rieul ? withB : withoutB;
  return b.has ? withB : withoutB;
}

/** 이름 + 조사. `withJosa('현대건설', '이/가')` → `현대건설이` */
export function withJosa(word: string, pair: JosaPair): string {
  return `${word}${josa(word, pair)}`;
}

// 병기 자리표시 → 조사쌍
const PLACEHOLDERS: Array<[RegExp, JosaPair]> = [
  [/(은\(는\)|는\(은\)|\(은\)는|\(는\)은)/, '은/는'],
  [/(이\(가\)|가\(이\)|\(이\)가|\(가\)이)/, '이/가'],
  [/(을\(를\)|를\(을\)|\(을\)를|\(를\)을)/, '을/를'],
  [/(과\(와\)|와\(과\)|\(과\)와|\(와\)과)/, '과/와'],
  [/(\(으\)로|으로\(로\)|로\(으로\))/, '으로/로'],
];

/** 본문에 남은 병기 자리표시 개수(게이트용 — CL fix_2 「은(는)류 0」). */
export function countJosaPlaceholders(text: string | null | undefined): number {
  const s = String(text ?? '');
  let n = 0;
  for (const [re] of PLACEHOLDERS) {
    const g = new RegExp(re.source, 'g');
    n += (s.match(g) || []).length;
  }
  return n;
}

/**
 * 「X은(는)」 「X(으)로」 같은 병기를 앞말 받침으로 확정한다.
 * 앞말은 자리표시 바로 앞의 공백 아닌 덩어리(괄호 묶음 포함)다.
 * hc4 통합(HC-U-⑤x 교차) — 자리표시 바로 앞 괄호 묶음에 공백이 끼면(「SKY (조합원 취소분)은(는)」·「SKY (본청약)은(는)」·
 *   「SKY(조합원 취소분)은(는)」) 공백 아닌 덩어리가 괄호 조각(「취소분)」·「(본청약)」)만 잡혀 판정이 틀렸다 →
 *   그 꼴만 괄호 앞 낱말로 먼저 판정한다. 공백 없는 괄호(「공공분양주택(본청약)은(는)」)는 종전 경로 그대로.
 */
const PAREN_BEFORE_PH = '([^\\s()（）]+)(\\s*[(（][^()（）]*[)）])';
export function fixJosaPlaceholders(text: string | null | undefined): string {
  let s = String(text ?? '');
  for (const [re, pair] of PLACEHOLDERS) {
    const gp = new RegExp(`${PAREN_BEFORE_PH}${re.source}`, 'g');
    s = s.replace(gp, (all: string, word: string, paren: string) => (/\s/.test(paren) ? `${word}${paren}${josa(word, pair)}` : all));
    const g = new RegExp(`([^\\s]+?)${re.source}`, 'g');
    s = s.replace(g, (_all: string, word: string) => `${word}${josa(word, pair)}`);
  }
  return s;
}
